import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  ChangeLink,
  EditPanel,
  SettingRow,
  StatusBanner,
  useInlineEdit,
  useSaveWithToast,
} from "./ui";
import { TemplateSlotCard } from "./template-slot-card";
import { SettingNames } from "./use-setting-names";

export type DocumentSlot = {
  source: "system" | "custom";
  /** The company's own template; only used when `source` is "custom". */
  template: string;
  /** The file registry series that numbers the generated document. */
  series: string;
  fileType: string;
  category: string;
};

type DocumentSlotRowProps = {
  label: string;
  /** Shown while a company's own template is chosen: it no longer follows system updates. */
  customTemplateWarning: string;
  slot: DocumentSlot;
  names: SettingNames;
  /** Opens the editor as soon as the page shows, for a link that points at this document. */
  openOnLoad?: boolean;
  onSave: (slot: DocumentSlot) => Promise<unknown>;
  /** Says why a save was refused. */
  saveErrorMessage: (err: unknown) => string;
};

/** One generated document: how it looks and where it is filed. */
export function DocumentSlotRow({
  label,
  customTemplateWarning,
  slot,
  names,
  openOnLoad,
  onSave,
  saveErrorMessage,
}: DocumentSlotRowProps) {
  const { t } = useTranslation();
  const edit = useInlineEdit(slot);
  const { save, saving, error } = useSaveWithToast();
  const panelRef = useRef<HTMLDivElement>(null);
  // Set by a link that points at this document, used up by the first scroll.
  const scrollPending = useRef(false);

  useEffect(() => {
    if (openOnLoad) {
      scrollPending.current = true;
      edit.start();
    }
    // Only on arrival: `start` is a new function on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOnLoad]);

  useEffect(() => {
    if (edit.editing && scrollPending.current) {
      scrollPending.current = false;
      panelRef.current?.scrollIntoView({ block: "center" });
    }
  }, [edit.editing]);

  const change = (fields: Partial<DocumentSlot>) =>
    edit.setDraft((draft) => ({ ...draft, ...fields }));

  const saveSlot = async () => {
    const saved = await save(() => onSave(edit.draft), {
      errorMessage: saveErrorMessage,
    });
    if (saved) edit.cancel();
  };

  const sourceName =
    slot.source === "custom"
      ? t("settings_sales_template_custom")
      : t("settings_sales_template_system");

  return (
    <SettingRow
      label={label}
      description={
        names.ready || !slot.category
          ? t("settings_sales_filed_in", {
              source: sourceName,
              category: names.categoryName(slot.category) || t("settings_none"),
            })
          : undefined
      }
      action={!edit.editing && <ChangeLink onClick={edit.start} />}
    >
      {edit.editing && (
        <div ref={panelRef}>
          <EditPanel
            saving={saving}
            error={error}
            onSave={saveSlot}
            onCancel={edit.cancel}
          >
            {edit.draft.source === "custom" && (
              <div className="mb-3">
                <StatusBanner tone="warn" title={customTemplateWarning} />
              </div>
            )}
            <TemplateSlotCard
              label={label}
              source={edit.draft.source}
              templateId={edit.draft.template}
              seriesId={edit.draft.series}
              fileTypeId={edit.draft.fileType}
              categoryId={edit.draft.category}
              seriesHint={t("settings_sales_registry_series_hint")}
              onSourceChange={(source) => change({ source })}
              onTemplateChange={(template) => change({ template })}
              onSeriesChange={(series) => change({ series })}
              onFileTypeChange={(fileType) => change({ fileType })}
              onCategoryChange={(category) => change({ category })}
            />
          </EditPanel>
        </div>
      )}
    </SettingRow>
  );
}
